export const STOREFRONT_ACCOUNT_FALLBACK = "/account";

export function isStorefrontAuthPath(pathname: string): boolean {
  return pathname === "/login" || pathname === "/register";
}

export function sanitizeInternalRedirect(
  target: string | null | undefined,
  fallback: string = STOREFRONT_ACCOUNT_FALLBACK
): string {
  if (!target || !target.startsWith("/") || target.startsWith("//")) {
    return fallback;
  }

  try {
    const url = new URL(target, "http://localhost");
    if (isStorefrontAuthPath(url.pathname)) {
      return fallback;
    }
  } catch {
    return fallback;
  }

  return target;
}

export function buildStorefrontLoginRedirectPath(pathname: string, search: string = ""): string {
  const returnTo = `${pathname}${search}`;

  if (!returnTo || isStorefrontAuthPath(pathname)) {
    return "/login";
  }

  const params = new URLSearchParams({ redirect: returnTo });
  return `/login?${params.toString()}`;
}

export function isProtectedStorefrontAccountPath(pathname: string): boolean {
  return pathname === "/account" || pathname.startsWith("/account/orders/");
}

interface ResolveStorefrontAuthRedirectInput {
  pathname: string;
  search?: string;
  hasSession: boolean;
  enforceServerGuard: boolean;
  redirectTarget?: string | null;
}

export function resolveStorefrontAuthRedirect({
  pathname,
  search = "",
  hasSession,
  enforceServerGuard,
  redirectTarget,
}: ResolveStorefrontAuthRedirectInput): string | null {
  if (!enforceServerGuard) {
    return null;
  }

  if (isProtectedStorefrontAccountPath(pathname) && !hasSession) {
    return buildStorefrontLoginRedirectPath(pathname, search);
  }

  if (isStorefrontAuthPath(pathname) && !hasSession && redirectTarget) {
    const normalizedRedirect = sanitizeInternalRedirect(redirectTarget, "");
    if (!normalizedRedirect) {
      return pathname;
    }
  }

  if (isStorefrontAuthPath(pathname) && hasSession) {
    return sanitizeInternalRedirect(redirectTarget, STOREFRONT_ACCOUNT_FALLBACK);
  }

  return null;
}
